// QUANDO o aviso de um compromisso da AGENDA sai — a regra, num lugar so.
//
// Decisao dele, 19/08/2026: *"ao criar a data e hora do compromisso, vamos criar
// tambem 'Avisar quando?'. Se deixar o campo em branco, avisa de manha as 06h00. Se
// preencher com a quantidade de horas de antecedencia, avisa na hora registrada."*
//
// ⚠️ **Este arquivo e' importado pelos DOIS lados**, e e' por isso que ele mora aqui:
//  - o SERVIDOR grava o `avisar_em` que o cron varre;
//  - a TELA escreve, embaixo do campo, *"Avisar: amanha 06:00"* — antes de salvar.
// Duas copias divergiriam no primeiro ajuste, e a que some e' sempre a do servidor
// (mesma licao da [DA-574] p.5). A tela dizendo uma hora e o cron disparando outra
// e' o defeito que ninguem relata: quem nao recebeu o aviso nao sabe que devia.
//
// ⚠️ **Nada aqui depende do fuso do PROCESSO.** As contas passam por
// `America/Sao_Paulo` explicito: o servidor roda em BRT hoje, mas "06:00" que vira
// 03:00 porque alguem subiu um container em UTC e' erro mudo — o aviso simplesmente
// chega na madrugada, e o cron continua verde.
//
// ⛔ Sem `Date.now()` aqui dentro: o `agora` sempre entra por parametro. E' o que
// torna o teste possivel sem congelar relogio, e o que impede a tela e o servidor de
// discordarem por causa do relogio do celular dele.

/** O fuso da loja. Uma constante, nao uma string repetida em cada `toLocaleString`. */
export const FUSO_LOJA = 'America/Sao_Paulo';

/** A hora do aviso quando ele deixa o campo em branco (escolha dele: 06:00). */
export const HORA_AVISO_PADRAO = 6;

/** Teto da antecedencia, em horas — 30 dias. Mesmo numero da trava do banco. */
export const AVISO_HORAS_MAX = 720;

/**
 * As tres alternativas do campo "Avisar quando?".
 *
 * ⚠️ Sao ALTERNATIVAS do mesmo ajuste, nao modos de uma operacao — a recusa de
 * "campo com modo" da [DA-574] e' sobre destruir x esconder, que sao opostos. Aqui
 * as tres respondem a mesma pergunta e nenhuma delas e' irreversivel.
 */
export type ModoAviso = 'padrao' | 'horas' | 'nenhum';

/**
 * Por que NAO ha aviso, quando nao ha.
 *
 * ⚠️ O motivo importa porque cada um vira uma FRASE diferente na tela. Devolver so
 * `null` faria os tres casos virarem o mesmo silencio, e o unico que ele precisa
 * enxergar — *"o horario que voce pediu ja passou"* — e' justamente o que some.
 */
export type MotivoSemAviso =
  /** Ele escolheu nao ser avisado. */
  | 'nenhum'
  /** O momento do aviso ja tinha passado quando ele salvou. */
  | 'ja_passou'
  /** O aviso cairia DEPOIS do proprio compromisso (compromisso antes das 06:00). */
  | 'depois_do_compromisso';

export interface ResultadoAviso {
  /** O instante do disparo. `null` = nao ha aviso a dar. */
  avisarEm: Date | null;
  /** Preenchido exatamente quando `avisarEm` e' `null`. */
  motivo: MotivoSemAviso | null;
}

// ---------------------------------------------------------------------------
// Fuso — as contas de calendario, sempre em America/Sao_Paulo
// ---------------------------------------------------------------------------

const FMT_PARTES = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_LOJA,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hourCycle: 'h23',
});

export interface PartesData {
  ano: number; mes: number; dia: number; hora: number; minuto: number; segundo: number;
}

/** O relogio de parede da loja naquele instante. */
export function partesNoFuso(d: Date): PartesData {
  const p = FMT_PARTES.formatToParts(d);
  const achar = (tipo: string): number => Number(p.find((x) => x.type === tipo)?.value ?? 0);
  return {
    ano: achar('year'), mes: achar('month'), dia: achar('day'),
    hora: achar('hour'), minuto: achar('minute'), segundo: achar('second'),
  };
}

/** Quantos minutos o fuso da loja esta a frente do UTC naquele instante. */
function deslocamentoMin(d: Date): number {
  const p = partesNoFuso(d);
  const comoUtc = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  // O `d` pode trazer milissegundos; a leitura acima nao. Truncar os dois no segundo
  // e' o que impede um deslocamento de "179,999 minutos" saindo de um arredondamento.
  return (comoUtc - Math.floor(d.getTime() / 1000) * 1000) / 60000;
}

/**
 * O instante em que o relogio da loja marca aquela data e hora.
 *
 * ⚠️ Duas passadas de proposito: a primeira usa o deslocamento do palpite (que pode
 * cair do lado errado de uma virada de horario de verao), a segunda usa o do
 * candidato. O Brasil nao tem horario de verao desde 2019, entao hoje a segunda
 * passada nunca corrige nada — ela existe pra o dia em que voltar, porque um erro
 * de 1 hora num aviso e' exatamente o tipo de coisa que ninguem liga ao calendario.
 */
export function instanteNoFuso(
  ano: number, mes: number, dia: number, hora: number, minuto: number,
): Date {
  const chute = Date.UTC(ano, mes - 1, dia, hora, minuto, 0);
  const off1 = deslocamentoMin(new Date(chute));
  const candidato = new Date(chute - off1 * 60000);
  const off2 = deslocamentoMin(candidato);
  return off2 === off1 ? candidato : new Date(chute - off2 * 60000);
}

/** `2026-08-19` no fuso da loja — a chave que agrupa a lista por dia. */
export function chaveDoDia(d: Date): string {
  const p = partesNoFuso(d);
  return `${p.ano}-${String(p.mes).padStart(2, '0')}-${String(p.dia).padStart(2, '0')}`;
}

/** `14:30` no fuso da loja. */
export function horaDoDia(d: Date): string {
  const p = partesNoFuso(d);
  return `${String(p.hora).padStart(2, '0')}:${String(p.minuto).padStart(2, '0')}`;
}

const FMT_DIA_CURTO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_LOJA, weekday: 'short', day: '2-digit', month: 'short',
});

/**
 * `hoje` / `amanha` / `ontem` / `qui, 21 de ago`.
 *
 * ⚠️ Os tres relativos existem porque sao os unicos que ele le sem contar dias no
 * dedo — e' a mesma razao de a lista comecar em HOJE em vez de numa grade de mes.
 */
export function rotuloDeDia(d: Date, agora: Date): string {
  const dia = chaveDoDia(d);
  const hoje = chaveDoDia(agora);
  if (dia === hoje) return 'hoje';
  const umDia = 24 * 60 * 60 * 1000;
  if (dia === chaveDoDia(new Date(agora.getTime() + umDia))) return 'amanhã';
  if (dia === chaveDoDia(new Date(agora.getTime() - umDia))) return 'ontem';
  // "qui., 21 de ago." -> "qui, 21 de ago": o ponto da abreviacao aparece duas vezes
  // na mesma linha curta e nao informa nada.
  return FMT_DIA_CURTO.format(d).replace(/\./g, '');
}

// ---------------------------------------------------------------------------
// A regra
// ---------------------------------------------------------------------------

/**
 * Quando o aviso daquele compromisso deve sair.
 *
 * As tres emendas que entraram junto com a decisao dele:
 *
 *  1. **Aviso cujo momento ja passou nao dispara** (`ja_passou`). Marcou hoje 14:00,
 *     as 13:30, pedindo 1h de antecedencia: o momento era 13:00. Nao ha aviso a
 *     dar — ele acabou de digitar aquilo, sabe do compromisso. ⚠️ O que NAO pode
 *     acontecer e' o cron achar essa linha vencida cinco minutos depois e mandar um
 *     "voce tem um compromisso" de algo que ele esta olhando: aviso atrasado ensina
 *     a desconfiar do relogio do app.
 *  2. **Aviso que cairia depois do compromisso tambem nao** (`depois_do_compromisso`).
 *     E' o caso do padrao das 06:00 num compromisso marcado pras 05:00.
 *  3. **O motivo volta junto**, pra tela poder dizer qual dos tres e'.
 *
 * ⚠️ O `agora` decide 1 e 2 — entao a MESMA linha, recalculada amanha, pode deixar
 * de ter aviso. E' por isso que o `avisar_em` e' recalculado em todo salvamento e
 * nunca "corrigido" pelo cron: quem varre so dispara o que ja foi decidido aqui.
 */
export function calcularAvisarEm(
  quando: Date,
  modo: ModoAviso,
  horas: number | null,
  agora: Date,
): ResultadoAviso {
  if (modo === 'nenhum') return { avisarEm: null, motivo: 'nenhum' };

  let alvo: Date;
  if (modo === 'horas') {
    // Numero ausente ou torto vira "sem aviso", nunca um NaN viajando ate o banco.
    // A trava de verdade e' o CHECK da migration 0239; isto e' a rede da tela.
    if (horas == null || !Number.isFinite(horas) || horas <= 0 || horas > AVISO_HORAS_MAX) {
      return { avisarEm: null, motivo: 'nenhum' };
    }
    alvo = new Date(quando.getTime() - horas * 60 * 60 * 1000);
  } else {
    const p = partesNoFuso(quando);
    alvo = instanteNoFuso(p.ano, p.mes, p.dia, HORA_AVISO_PADRAO, 0);
  }

  if (alvo.getTime() > quando.getTime()) {
    return { avisarEm: null, motivo: 'depois_do_compromisso' };
  }
  if (alvo.getTime() <= agora.getTime()) {
    return { avisarEm: null, motivo: 'ja_passou' };
  }
  return { avisarEm: alvo, motivo: null };
}

/**
 * A frase que fica embaixo do campo "Avisar quando?".
 *
 * ⚠️ Ela existe porque **a tela dizer o horario resolve todo caso torto sem regra
 * escondida**: o compromisso as 05:00 com o padrao das 06:00, a antecedencia de 3h
 * num compromisso que e' daqui a 1h. Ele le e corrige, em vez de descobrir dias
 * depois que o aviso nunca veio. Botao/campo que trava DIZ por que (regra da casa,
 * § UI) — aqui nao ha nada travado, mas o silencio custaria igual.
 */
export function descreverAviso(r: ResultadoAviso, agora: Date): string {
  if (r.avisarEm) return `Avisar: ${rotuloDeDia(r.avisarEm, agora)} ${horaDoDia(r.avisarEm)}`;
  if (r.motivo === 'nenhum') return 'Sem aviso';
  if (r.motivo === 'depois_do_compromisso') return 'Esse aviso sairia depois do compromisso — sem aviso';
  return 'Esse horário já passou — sem aviso';
}
